import { Card, Form, FormButtonRow, IconButton } from '@ksyos/design-system';
import { useCallback, useEffect, useState } from 'react';
import { FormParagraphFormGroupField } from '@frontend/components/fields/FormParagraphField';
import type { ValidationErrors } from '../../../utils/validation';
import type { IEmployment } from '../../interfaces/employment';
import { employmentContactDetailsSchema } from '../../../../common/validation/employment';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { FormContextProvider } from '../../elements/formContext';
import { CancelButton } from '../../userDataManagement/CancelButton';
import { InputTextField } from '../../userDataManagement/InputText';
import { SaveButton } from '../../userDataManagement/SaveButton';

interface IContactDetailsProps {
    employmentId: string;
    enableEditByDefault?: boolean;
    focusFieldByDefault?: string;
    isReadOnly: boolean;
}

export function ContactDetails(props: IContactDetailsProps) {
    const abortableFetch = useAbortableFetch();
    const [isEditing, setIsEditing] = useState(props.enableEditByDefault ?? false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [originalEmployment, setOriginalEmployment] = useState<IEmployment>();
    const [employment, setEmployment] = useState<IEmployment>();

    const contactDetailsForm = useCallback(
        (contactFormNode: HTMLFormElement) => {
            if (contactFormNode !== null && props.enableEditByDefault && props.focusFieldByDefault) {
                const input: HTMLInputElement | null = contactFormNode.querySelector(
                    `input[name=${props.focusFieldByDefault}]`
                );
                if (input) {
                    input.focus();
                }
            }
        },
        [props.enableEditByDefault, props.focusFieldByDefault]
    );

    useEffect(() => {
        loadEmployment(props.employmentId);
    }, [props.employmentId]);

    if (!employment) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'contactDetails',
                item: employment,
                errors: errors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !props.isReadOnly && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translateKey('personalSettings.contactDetails')}
                />
                <Card.Body>
                    <Form
                        formRef={contactDetailsForm}
                        submitOnEnter={false}
                    >
                        {renderTextInput('emailAddress', 'common.email')}
                        {renderTextInput('emailAddressAlt', 'personalSettings.emailAlt')}
                        {renderTextInput('emergencyNumber', 'personalSettings.emergencyNumber')}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => handleCancel()}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    function renderTextInput(fieldKey: string, label: string) {
        if (isEditing) {
            return (
                <InputTextField
                    dontValidateOnBlur
                    error={errors.get(fieldKey)}
                    fieldKey={fieldKey}
                    hasErrors={errors.has(fieldKey)}
                    label={label}
                    nullable
                />
            );
        }

        return (
            <FormParagraphFormGroupField
                fieldKey={fieldKey}
                label={label}
            />
        );
    }

    async function loadEmployment(id: string) {
        const url = `/api/employments/${id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOriginalEmployment(result.json);
        setEmployment(result.json);
    }

    function handleChange<K extends keyof IEmployment>(name: K, value: IEmployment[K]) {
        setEmployment({ ...employment, [name]: value } as IEmployment);
    }

    function validateOne(name: keyof IEmployment) {
        setErrors(updateValidationErrorsForOne(employmentContactDetailsSchema, employment, errors, name));
    }

    function handleCancel() {
        setEmployment(originalEmployment);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (employment) {
            setIsSaving(true);

            const { errors: validationErrors, item } = validate(employmentContactDetailsSchema, employment);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                const url = `/api/employments/${employment.id}/contactDetails`;
                await abortableFetch.fetchJson(url, 'POST', item);
                setOriginalEmployment(employment);
                setErrors(emptyValidationErrors);
                setIsEditing(false);
            }

            setIsSaving(false);
        }
    }
}
